import React, { useEffect, useState } from "react";
import axios from "axios";
import UpdateBlog from "./UpdateBlog";
import { config } from "../../../config";
import Compressor from "compressorjs";
import DOMPurify from 'dompurify';




const apiUrl = config.apiUrl;

const ViewBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    content: "",
    is_featured: false,
    category_id: "",
    author_id: "",
  });

  // Fetch blogs from the API
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${apiUrl}/blogs`);
        setBlogs(response.data.blogs);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setLoading(false);
        setError("Failed to load blog details.");
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const authorsRes = await axios.get(`${apiUrl}/users`);
        const categoriesRes = await axios.get(`${apiUrl}/categories`);
        setAuthors(authorsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (err) {
        setError("An error occurred while fetching data.");
      }
    };
    fetchData();
  }, []);

    const cleanHTML = (html) => {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return div.innerText || "";
    };

  // Handle Edit Click - Open Modal
  const handleEditClick = (blog) => {
    setEditingBlog(blog.id);
    setFormData({
      slug: blog.slug,
      title: blog.title,
      content: cleanHTML(blog.content || ""),
      image: blog.image,
      is_featured: blog.is_featured,
      category_id: blog.category_id,
      author_id: blog.author_id,
    });
    setIsModalOpen(true);
  };

  // Close Modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBlog(null);
  };

  

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value || "",
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile.size > 500 * 1024) {
      new Compressor(selectedFile, {
        quality: 0.6,
        convertSize: 500 * 1024,
        mimeType: 'image/webp',
        success(result) {
          const webpFile = new File([result], selectedFile.name.replace(/\.[^/.]+$/, ".webp"), {
            type: 'image/webp',
          });
          setImageFile(webpFile);
        },
        error(err) {
          console.error("Compression error:", err);
        },
      });
    } else {
      setImageFile(selectedFile);
    }
  };

  // Handle Delete Blog
  const handleDelete = async (blogId) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      try {
        await axios.delete(`${apiUrl}/blogdelete/${blogId}`);
        alert("Blog deleted successfully");
        setBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
      } catch (err) {
        console.error("Error deleting blog:", err);
        setError("An error occurred while deleting the blog.");
      }
    }
  };

  // Handle Form Submit for Updating Blog
  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("_method", "PUT");
    data.append("slug", formData.slug);
    data.append("title", formData.title);
    data.append("content", formData.content);
    data.append("is_featured", formData.is_featured ? 1 : 0);
    data.append("author_id", formData.author_id);
    data.append("category_id", formData.category_id);
    
    if (imageFile) {
        data.append("image", imageFile);
    }

    try {
      await axios.post(`${apiUrl}/blogsupdate/${editingBlog}`, data , {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Blog updated successfully");

      setBlogs((prev) =>
        prev.map((blog) =>
          blog.id === editingBlog ? { ...blog, ...formData } : blog
        )
      );

      closeModal();
    } catch (err) {
      console.error("Error updating blog:", err);
      setError("An error occurred while updating the blog.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Modal Backdrop */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white">
            {/* Modal Content */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Edit Blog</h3>
              <button 
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <UpdateBlog
              formData={formData}
              handleQuillChange={handleQuillChange}
              handleFileChange={handleFileChange}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              error={error}
              authors={authors}
              categories={categories}
              closeModal={closeModal}
              editingBlog={editingBlog}
              
            />

          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Blog Management</h2>
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
              <p>{error}</p>
            </div>
          )}
        </div>

        {blogs.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No blogs found</h3>
            <p className="mt-1 text-gray-500">Get started by creating a new blog post.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Content Preview
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Featured
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blogs.map((blog) => {
                  const categoryName = categories?.find((cat) => cat.id === blog.category_id)?.name || "Unknown";
                  const authorName = authors?.find((author) => author.id === blog.author_id)?.name || "Unknown";
                 const contentPreview = blog.content && blog.content.length > 50 
                    ? `${blog.content.substring(0, 50)}...` 
                    : blog.content || "No content";

                  return (
                    <tr key={blog.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{contentPreview}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${blog.is_featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {blog.is_featured ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{categoryName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{authorName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditClick(blog)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(blog.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewBlog;